import React, { useEffect, useState } from "react";
import worklistApi from "../services/worklistApi";
import { ColumnConfig, Option, WorklistFilter } from "../types";
import { FilterValue, describeFilter, isAbort, parseOptions } from "../utils";
import FilterPopover, { PopoverMode } from "./FilterPopover";
import useLang from "Shared/hooks/useLanguage";

interface Props {
  tabId: number;
  columns: ColumnConfig[]; // all columns of the tab
  filters: WorklistFilter[];
  onChange: (column: ColumnConfig, value: FilterValue | null) => void;
}

const FilterChips = ({ tabId, columns, filters, onChange }: Props) => {
  const { t } = useLang();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<PopoverMode | null>(null);
  // Options per column id: undefined = not loaded yet, null = failed.
  const [options, setOptions] = useState<Record<number, Option[] | null>>({});

  const filterable = columns.filter((c) => c.filterType && !c.isExpand);
  useEffect(() => setOptions({}), [tabId]);

  // Lazy options: use what TabColumnsV2 sent, otherwise call FilterOptions when needed.
  const needOptions = [
    ...filters.map((f) => columns.find((c) => c.id === f.id)),
    mode?.kind === "edit" ? mode.column : undefined,
  ].filter(
    (c): c is ColumnConfig =>
      !!c &&
      (c.filterType === "dropdown" || c.filterType === "multiselect") &&
      !(c.id in options)
  );
  const needKey = needOptions.map((c) => c.id).join(",");

  useEffect(() => {
    if (!needOptions.length) return;
    const ac = new AbortController();
    needOptions.forEach((c) => {
      const fromConfig = parseOptions(c.optionsJson);
      if (fromConfig.length) {
        setOptions((o) => ({ ...o, [c.id]: fromConfig }));
        return;
      }
      worklistApi
        .filterOptions(tabId, c.id, ac.signal)
        .then((json) => setOptions((o) => ({ ...o, [c.id]: parseOptions(json) })))
        .catch((e) => {
          if (!isAbort(e)) setOptions((o) => ({ ...o, [c.id]: null }));
        });
    });
    return () => ac.abort();
  }, [needKey, tabId]);

  const open = (el: HTMLElement, m: PopoverMode) => {
    setAnchorEl(el);
    setMode(m);
  };
  const close = () => {
    setAnchorEl(null);
    setMode(null);
  };
  const remove = (e: React.MouseEvent, column: ColumnConfig) => {
    e.stopPropagation();
    onChange(column, null);
  };

  return (
    <div className="vr2-chips">
      {filters.map((f) => {
        const c = columns.find((x) => x.id === f.id);
        if (!c) return null;
        return (
          <span
            key={f.id}
            role="button"
            tabIndex={0}
            className="vr2-chip"
            onClick={(e) => open(e.currentTarget, { kind: "edit", column: c })}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              open(e.currentTarget, { kind: "edit", column: c })
            }
          >
            <span className="vr2-chip-label">{t(c.label)}:</span>
            <span className="vr2-chip-value">
              {describeFilter(c, f, options[c.id] ?? [])}
            </span>
            {c.filterType === "text" && (
              <span
                className="vr2-chip-warn"
                title={t("Contains-search cannot use an index")}
              >
                <i className="bi bi-exclamation-triangle"></i>
              </span>
            )}
            <button
              type="button"
              className="vr2-chip-remove"
              aria-label={`${t("Remove")} ${t(c.label)} ${t("filter")}`}
              onClick={(e) => remove(e, c)}
            >
              <i className="bi bi-x"></i>
            </button>
          </span>
        );
      })}

      {filterable.length > 0 && (
        <button
          type="button"
          id="ViewRequisitionV2AddFilter"
          className="vr2-chip is-add"
          onClick={(e) => open(e.currentTarget, { kind: "pick" })}
        >
          <i className="bi bi-plus-lg"></i> {t("Filter")}
        </button>
      )}

      <FilterPopover
        anchorEl={anchorEl}
        mode={mode}
        columns={filterable}
        initial={
          mode?.kind === "edit"
            ? filters.find((f) => f.id === mode.column.id)
            : undefined
        }
        options={mode?.kind === "edit" ? options[mode.column.id] : undefined}
        onPick={(column) => setMode({ kind: "edit", column })}
        onApply={(column, value) => {
          onChange(column, value);
          close();
        }}
        onClose={close}
      />
    </div>
  );
};

export default FilterChips;
